import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CARD_DEFS, TIME_SLOTS, DEFAULT_LAYOUT, normalizeLayout, findSticker, stickerSrc, cachedLayout, cacheLayout,
} from './homeLayout';

const API = process.env.REACT_APP_BACKEND_URL;

// Settings → stickers & cards: your homepage stickers (upload, replace,
// caption, which ones the big sticker cycles through), where each one goes,
// and the homepage cards' order, size and whether they show. Every change
// saves on its own.

const Icon = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const UpIcon = (p) => <Icon {...p}><path d="M6 15l6-6 6 6" /></Icon>;
const DownIcon = (p) => <Icon {...p}><path d="M6 9l6 6 6-6" /></Icon>;
const EyeIcon = (p) => <Icon {...p}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></Icon>;
const EyeOffIcon = (p) => <Icon {...p}><path d="M3 3l18 18" /><path d="M10.6 5.1A10 10 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.1 3.9M6.6 6.6A17 17 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.4-1.6" /><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" /></Icon>;
const PlusIcon = (p) => <Icon {...p}><path d="M12 5v14" /><path d="M5 12h14" /></Icon>;
const SwapIcon = (p) => <Icon {...p}><path d="M4 7h13l-3-3" /><path d="M20 17H7l3 3" /></Icon>;
const TrashIcon = (p) => <Icon {...p}><path d="M4 7h16" /><path d="M10 11v6M14 11v6" /><path d="M6 7l1 13h10l1-13" /><path d="M9 7V4h6v3" /></Icon>;
const ResetIcon = (p) => <Icon {...p}><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></Icon>;
const CheckIcon = (p) => <Icon {...p}><path d="M5 12l5 5 9-10" /></Icon>;

const newId = () => `u${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export default function HomeSettings() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [layout, setLayout] = useState(cachedLayout);
  const [saveState, setSaveState] = useState('');        // '' | saving | saved | error
  const [picker, setPicker] = useState(null);            // { title, value, allowNone, onPick }
  const [uploading, setUploading] = useState('');        // '' | 'new' | <sticker id>
  const [errorMsg, setErrorMsg] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);
  const saveTimer = useRef(null);
  const newFile = useRef(null);
  const replaceFile = useRef(null);
  const replaceTarget = useRef(null);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    axios.get(`${API}/home/layout?token=${token}`)
      .then(res => apply(normalizeLayout(res.data.layout)))
      .catch(() => setErrorMsg("couldn't load your homepage settings — showing this device's copy"));
  }, []);

  // every change saves itself, a moment after you stop tapping. The ref
  // always holds the newest layout, so quick taps and uploads never undo each other.
  const layoutRef = useRef(layout);
  const apply = (next) => { layoutRef.current = next; setLayout(next); cacheLayout(next); };
  const change = (fn) => {
    const next = normalizeLayout(fn(JSON.parse(JSON.stringify(layoutRef.current))));
    apply(next);
    clearTimeout(saveTimer.current);
    setSaveState('saving');
    saveTimer.current = setTimeout(() => {
      axios.put(`${API}/home/layout`, { token, layout: layoutRef.current })
        .then(() => { setSaveState('saved'); setTimeout(() => setSaveState(v => (v === 'saved' ? '' : v)), 1600); })
        .catch(err => { setSaveState('error'); setErrorMsg(err?.response?.data?.detail || "that didn't save — try again"); });
    }, 600);
  };

  const upload = (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/home/stickers?token=${token}`, fd).then(res => res.data.url);
  };

  const onNewFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading('new'); setErrorMsg('');
    try {
      for (const f of files) {
        const url = await upload(f);
        change(l => { l.stickers.push({ id: newId(), url, caption: '', builtin: false, rotation: true }); return l; });
      }
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || "that image didn't upload");
    } finally {
      setUploading('');
    }
  };

  const onReplaceFile = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = '';
    const id = replaceTarget.current;
    if (!file || !id) return;
    setUploading(id); setErrorMsg('');
    try {
      const url = await upload(file);
      change(l => { const st = l.stickers.find(x => x.id === id); if (st) st.url = url; return l; });
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || "that image didn't upload");
    } finally {
      setUploading('');
    }
  };

  const moveCard = (i, dir) => change(l => {
    const j = i + dir;
    if (j < 0 || j >= l.cards.length) return l;
    [l.cards[i], l.cards[j]] = [l.cards[j], l.cards[i]];
    return l;
  });

  const removeSticker = (id) => change(l => {
    l.stickers = l.stickers.filter(x => x.id !== id);
    Object.keys(l.greeting).forEach(k => { if (l.greeting[k] === id) l.greeting[k] = DEFAULT_LAYOUT.greeting[k]; });
    Object.keys(l.card_stickers).forEach(k => { if (l.card_stickers[k] === id) l.card_stickers[k] = null; });
    if (l.peeker === id) l.peeker = null;
    return l;
  });

  const resetAll = () => {
    axios.delete(`${API}/home/layout?token=${token}`)
      .then(() => { apply(normalizeLayout(null)); setConfirmReset(false); setSaveState('saved'); })
      .catch(() => setErrorMsg("couldn't reset it — try again"));
  };

  const thumb = (id, size = 46) => {
    const st = findSticker(layout, id);
    return st
      ? <img src={stickerSrc(st)} alt="" style={{ width: size, height: size, objectFit: 'contain' }} onError={e => { e.currentTarget.style.opacity = 0.3; }} />
      : <span style={{ ...s.none, width: size, height: size }}>none</span>;
  };

  const slotRow = (label, hint, value, allowNone, onPick) => (
    <button key={label} className="hs-btn" style={s.slot} onClick={() => setPicker({ title: label, value, allowNone, onPick })}>
      <span style={s.slotThumb}>{thumb(value, 44)}</span>
      <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
        <span style={s.slotLabel}>{label}</span>
        {hint && <span style={s.slotHint}>{hint}</span>}
      </span>
      <span style={s.change}>change</span>
    </button>
  );

  const shown = layout.cards.filter(c => c.show);

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap" rel="stylesheet" />
      <style>{`
        .hs-btn:focus-visible { outline: 2px solid #c9935a; outline-offset: 2px; }
        .hs-in:focus { border-color: rgba(201,147,90,0.5) !important; outline: none; }
        .hs-in::placeholder { color: #5d4f44; }
      `}</style>

      <header style={s.header}>
        <button className="hs-btn" style={s.back} onClick={() => navigate('/settings')} aria-label="back">←</button>
        <div style={s.title}>stickers & cards</div>
        <span style={s.saveState} role="status">
          {saveState === 'saving' ? 'saving…' : saveState === 'saved' ? <><CheckIcon size={13} /> saved</> : ''}
        </span>
      </header>

      <main style={s.main}>
        {errorMsg && <div style={s.error} onClick={() => setErrorMsg('')}>{errorMsg}</div>}

        {/* ---------------- cards ---------------- */}
        <section style={s.section}>
          <div style={s.label}>homepage cards</div>
          <div style={s.hint}>top to bottom, the way they'll appear. half cards sit two to a row.</div>
          <div style={s.cardList}>
            {layout.cards.map((c, i) => {
              const def = CARD_DEFS.find(d => d.key === c.key);
              return (
                <div key={c.key} style={s.cardRow(c.show)}>
                  <div style={s.arrows}>
                    <button className="hs-btn" style={s.arrowBtn} onClick={() => moveCard(i, -1)} disabled={i === 0} aria-label={`move ${def.title} up`}><UpIcon size={15} /></button>
                    <button className="hs-btn" style={s.arrowBtn} onClick={() => moveCard(i, 1)} disabled={i === layout.cards.length - 1} aria-label={`move ${def.title} down`}><DownIcon size={15} /></button>
                  </div>
                  <span style={s.cardName}>{def.title}</span>
                  <div style={s.seg} role="group" aria-label={`${def.title} size`}>
                    {['half', 'full'].map(size => (
                      <button key={size} className="hs-btn" style={s.segBtn(c.size === size)} aria-pressed={c.size === size}
                        onClick={() => change(l => { l.cards[i].size = size; return l; })}>{size}</button>
                    ))}
                  </div>
                  <button className="hs-btn" style={s.eye(c.show)} aria-pressed={!c.show}
                    onClick={() => change(l => { l.cards[i].show = !l.cards[i].show; return l; })}
                    aria-label={c.show ? `hide ${def.title}` : `show ${def.title}`} title={c.show ? 'showing — tap to hide' : 'hidden — tap to show'}>
                    {c.show ? <EyeIcon /> : <EyeOffIcon />}
                  </button>
                </div>
              );
            })}
          </div>

          <div style={s.previewLabel}>how it'll look</div>
          <div style={s.preview} aria-hidden="true">
            {shown.map(c => {
              const st = findSticker(layout, layout.card_stickers[c.key]);
              return (
                <div key={c.key} style={s.previewCard(c.size === 'full')}>
                  <span>{CARD_DEFS.find(d => d.key === c.key).title}</span>
                  {st && <img src={stickerSrc(st)} alt="" style={s.previewSticker} />}
                </div>
              );
            })}
            {!shown.length && <div style={s.hint}>every card is hidden</div>}
          </div>
        </section>

        {/* ---------------- where stickers go ---------------- */}
        <section style={s.section}>
          <div style={s.label}>the big sticker</div>
          <div style={s.hint}>the one next to the greeting starts on these, depending on the time. tapping it goes through the stickers ticked below.</div>
          {TIME_SLOTS.map(t => slotRow(t.label, t.hint, layout.greeting[t.key], false,
            id => change(l => { l.greeting[t.key] = id; return l; })))}
        </section>

        <section style={s.section}>
          <div style={s.label}>on the cards</div>
          {CARD_DEFS.map(d => slotRow(d.title, null, layout.card_stickers[d.key], true,
            id => change(l => { l.card_stickers[d.key] = id; return l; })))}
          <div style={s.label}>peeking from the corner</div>
          {slotRow('bottom corner', 'the one that says hi when you tap it', layout.peeker, true,
            id => change(l => { l.peeker = id; return l; }))}
        </section>

        {/* ---------------- the stickers themselves ---------------- */}
        <section style={s.section}>
          <div style={s.rowBetween}>
            <div style={s.label}>your stickers</div>
            <button className="hs-btn" style={s.addBtn} onClick={() => newFile.current?.click()} disabled={!!uploading}>
              <PlusIcon size={14} /> {uploading === 'new' ? 'uploading…' : 'upload'}
            </button>
          </div>
          <div style={s.hint}>see-through backgrounds stay see-through. the caption shows under the greeting.</div>
          <input ref={newFile} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={onNewFiles} />
          <input ref={replaceFile} type="file" accept="image/*" style={{ display: 'none' }} onChange={onReplaceFile} />
          <div style={s.stickerGrid}>
            {layout.stickers.map(st => (
              <div key={st.id} style={s.stickerCard}>
                <div style={s.stickerImgWrap}>
                  <img src={stickerSrc(st)} alt="" style={s.stickerImg} onError={e => { e.currentTarget.style.opacity = 0.25; }} />
                  {uploading === st.id && <div style={s.uploadingVeil}>uploading…</div>}
                </div>
                <input className="hs-in" style={s.caption} value={st.caption} placeholder="a caption" aria-label="caption"
                  onChange={e => { const v = e.target.value; change(l => { l.stickers.find(x => x.id === st.id).caption = v.slice(0, 80); return l; }); }} />
                <label style={s.tick}>
                  <input type="checkbox" checked={st.rotation} style={{ accentColor: '#c9935a' }}
                    onChange={e => { const v = e.target.checked; change(l => { l.stickers.find(x => x.id === st.id).rotation = v; return l; }); }} />
                  in the tap rotation
                </label>
                <div style={s.stickerBtns}>
                  <button className="hs-btn" style={s.miniBtn} disabled={!!uploading} title="replace the image"
                    onClick={() => { replaceTarget.current = st.id; replaceFile.current?.click(); }}><SwapIcon size={13} /> replace</button>
                  {st.builtin
                    ? (st.url && <button className="hs-btn" style={s.miniBtn} title="back to the original image"
                        onClick={() => change(l => { l.stickers.find(x => x.id === st.id).url = null; return l; })}><ResetIcon size={13} /> original</button>)
                    : <button className="hs-btn" style={{ ...s.miniBtn, color: '#c98a7a' }} onClick={() => removeSticker(st.id)} aria-label="remove sticker"><TrashIcon size={13} /></button>}
                </div>
              </div>
            ))}
          </div>
        </section>

        <div style={{ textAlign: 'center' }}>
          {!confirmReset
            ? <button className="hs-btn" style={s.quiet} onClick={() => setConfirmReset(true)}>put everything back the way it was</button>
            : (
              <div style={s.confirm}>
                <div>go back to the original cards and stickers? your uploaded images are forgotten.</div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '10px' }}>
                  <button className="hs-btn" style={s.danger} onClick={resetAll}>reset</button>
                  <button className="hs-btn" style={s.quietBox} onClick={() => setConfirmReset(false)}>keep mine</button>
                </div>
              </div>
            )}
        </div>
      </main>

      {picker && (
        <div style={s.overlay} onClick={() => setPicker(null)}>
          <div style={s.sheet} onClick={e => e.stopPropagation()} role="dialog" aria-label={`sticker for ${picker.title}`}>
            <div style={s.sheetHead}>
              <div style={s.sheetTitle}>{picker.title}</div>
              <button className="hs-btn" style={s.back} onClick={() => setPicker(null)} aria-label="close">×</button>
            </div>
            <div style={s.pickGrid}>
              {picker.allowNone && (
                <button className="hs-btn" style={s.pick(picker.value === null)} onClick={() => { picker.onPick(null); setPicker(null); }}>
                  <span style={{ ...s.none, width: 64, height: 64 }}>none</span>
                </button>
              )}
              {layout.stickers.map(st => (
                <button key={st.id} className="hs-btn" style={s.pick(picker.value === st.id)} aria-pressed={picker.value === st.id}
                  onClick={() => { picker.onPick(st.id); setPicker(null); }} aria-label={st.caption || 'sticker'}>
                  <img src={stickerSrc(st)} alt="" style={{ width: 64, height: 64, objectFit: 'contain' }} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const serif = "'Cormorant Garamond', serif";
const sans = "'DM Sans', sans-serif";
const s = {
  page: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#0e0b0f', color: '#e8ddd0', fontFamily: sans, fontWeight: 300 },
  header: { position: 'sticky', top: 0, zIndex: 10, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '12px', background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', borderBottom: '1px solid rgba(201,147,90,0.06)' },
  back: { width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#c9935a', fontSize: '16px', cursor: 'pointer', flexShrink: 0 },
  title: { flex: 1, fontFamily: serif, fontSize: '21px', fontStyle: 'italic' },
  saveState: { display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: '#7fa37a', minWidth: '60px', justifyContent: 'flex-end' },
  main: { maxWidth: '680px', margin: '0 auto', padding: '22px 20px 90px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '16px' },
  section: { background: '#1e1825', border: '1px solid rgba(201,147,90,0.08)', borderRadius: '20px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '10px' },
  label: { fontSize: '10px', color: '#8a7466', letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: '2px' },
  hint: { fontSize: '12px', color: '#7a6a5a', lineHeight: 1.55 },
  rowBetween: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' },
  cardList: { display: 'flex', flexDirection: 'column', gap: '6px' },
  cardRow: (show) => ({ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '14px', background: '#17131b', opacity: show ? 1 : 0.55 }),
  arrows: { display: 'flex', flexDirection: 'column', gap: '2px' },
  arrowBtn: { width: '30px', height: '22px', borderRadius: '8px', border: 'none', background: 'rgba(201,147,90,0.08)', color: '#c9a57a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
  cardName: { flex: 1, minWidth: 0, fontFamily: serif, fontSize: '19px', fontStyle: 'italic' },
  seg: { display: 'flex', gap: '4px', padding: '3px', borderRadius: '12px', background: '#0e0b0f' },
  segBtn: (on) => ({ padding: '6px 12px', borderRadius: '9px', border: 'none', background: on ? 'linear-gradient(135deg, #8a5f35, #c9935a)' : 'transparent', color: on ? '#0e0b0f' : '#a8927f', fontSize: '12px', cursor: 'pointer', fontFamily: sans }),
  eye: (on) => ({ width: '34px', height: '34px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.18)', background: 'transparent', color: on ? '#c9935a' : '#6d5d4e', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }),
  previewLabel: { fontSize: '10px', color: '#6d5d4e', letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: '8px' },
  preview: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', padding: '10px', borderRadius: '14px', background: '#0e0b0f' },
  previewCard: (full) => ({ position: 'relative', gridColumn: full ? '1 / -1' : 'auto', minHeight: '38px', borderRadius: '9px', background: '#1e1825', border: '1px solid rgba(201,147,90,0.14)', display: 'flex', alignItems: 'center', padding: '0 10px', fontFamily: serif, fontStyle: 'italic', fontSize: '14px', color: '#c9b8a6' }),
  previewSticker: { position: 'absolute', right: '4px', top: '-6px', width: '30px', height: '30px', objectFit: 'contain' },
  slot: { display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px 8px 8px', borderRadius: '14px', border: 'none', background: '#17131b', color: '#e0d4c6', cursor: 'pointer', fontFamily: sans, width: '100%' },
  slotThumb: { width: '52px', height: '52px', borderRadius: '12px', background: '#0e0b0f', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  slotLabel: { display: 'block', fontSize: '14px' },
  slotHint: { display: 'block', fontSize: '11.5px', color: '#7a6a5a', marginTop: '2px' },
  change: { fontSize: '12px', color: '#c9935a' },
  none: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '10px', border: '1px dashed rgba(201,147,90,0.3)', color: '#6d5d4e', fontSize: '11px' },
  addBtn: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f', fontSize: '12.5px', cursor: 'pointer', fontFamily: sans },
  stickerGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' },
  stickerCard: { display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px', borderRadius: '14px', background: '#17131b' },
  stickerImgWrap: { position: 'relative', height: '110px', borderRadius: '10px', background: 'repeating-conic-gradient(#141018 0% 25%, #1a151f 0% 50%) 50% / 16px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  stickerImg: { maxWidth: '100%', maxHeight: '100px', objectFit: 'contain' },
  uploadingVeil: { position: 'absolute', inset: 0, borderRadius: '10px', background: 'rgba(14,11,15,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#c9a57a' },
  caption: { width: '100%', boxSizing: 'border-box', background: '#0e0b0f', border: '1px solid rgba(201,147,90,0.14)', borderRadius: '9px', padding: '7px 9px', color: '#e8ddd0', fontSize: '12.5px', fontFamily: sans },
  tick: { display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', color: '#a8927f', cursor: 'pointer' },
  stickerBtns: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  miniBtn: { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '5px 9px', borderRadius: '9px', border: '1px solid rgba(201,147,90,0.18)', background: 'transparent', color: '#c9a57a', fontSize: '11.5px', cursor: 'pointer', fontFamily: sans },
  quiet: { background: 'none', border: 'none', color: '#8a7466', fontSize: '12.5px', cursor: 'pointer', fontFamily: sans, textDecoration: 'underline', textUnderlineOffset: '3px' },
  quietBox: { padding: '8px 16px', borderRadius: '12px', border: '1px solid rgba(201,147,90,0.2)', background: 'transparent', color: '#c9a57a', fontSize: '13px', cursor: 'pointer', fontFamily: sans },
  confirm: { padding: '14px', borderRadius: '14px', background: 'rgba(180,60,60,0.08)', border: '1px solid rgba(220,100,100,0.2)', fontSize: '13px', color: '#e0c0b0', lineHeight: 1.5 },
  danger: { padding: '8px 16px', borderRadius: '12px', border: 'none', background: '#a0473a', color: '#fff', fontSize: '13px', cursor: 'pointer', fontFamily: sans },
  overlay: { position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(8,6,10,0.72)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  sheet: { width: '100%', maxWidth: '560px', maxHeight: '80vh', overflowY: 'auto', background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)', borderRadius: '22px 22px 0 0', padding: '18px 18px 28px', boxSizing: 'border-box' },
  sheetHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' },
  sheetTitle: { fontFamily: serif, fontSize: '22px', fontStyle: 'italic' },
  pickGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))', gap: '8px' },
  pick: (on) => ({ height: '84px', borderRadius: '14px', border: on ? '2px solid #c9935a' : '1px solid rgba(201,147,90,0.12)', background: on ? 'rgba(201,147,90,0.1)' : '#17131b', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }),
  error: { background: 'rgba(180,60,60,0.12)', border: '1px solid rgba(220,100,100,0.3)', borderRadius: '12px', padding: '10px 14px', fontSize: '12px', color: '#e0a0a0', cursor: 'pointer' },
};
